import type {SxProps, Theme} from "@mui/material";
import {ELLIPSIS} from "../../utils/str.utils";

const CATEGORY_CHIP_ARROW_WIDTH = 16;
const CATEGORY_CHIP_GAP = 4;
const CATEGORY_CHIP_MIN_WIDTH = 114;
const CATEGORY_CHIP_LABEL_PADDING_X = 10;
const CATEGORY_CHIP_ELLIPSIS_START = 3;
const CATEGORY_CHIP_ELLIPSIS_END = 6;
const CATEGORY_CHIP_LABEL_FONT = '400 13px Roboto, Helvetica, Arial, sans-serif';
const CATEGORY_CHIP_FALLBACK_CHAR_WIDTH = 5;

const getCategoryChipMeasureContext = (() => {
    let ctx: CanvasRenderingContext2D | null | undefined;

    return (): CanvasRenderingContext2D | null => {
        if (ctx !== undefined) return ctx;
        if (typeof document === 'undefined') {
            ctx = null;
            return null;
        }
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) context.font = CATEGORY_CHIP_LABEL_FONT;
        ctx = context;
        return ctx;
    };
})();

function measureCategoryChipLabelWidth(text: string): number {
    const ctx = getCategoryChipMeasureContext();
    if (ctx) return ctx.measureText(text).width;
    return text.length * CATEGORY_CHIP_FALLBACK_CHAR_WIDTH;
}

function categoryChipLabelMaxWidth(chipWidthPx: number): number {
    return chipWidthPx - (2 * CATEGORY_CHIP_LABEL_PADDING_X);
}

function categoryChipTextFits(text: string, chipWidthPx: number): boolean {
    return measureCategoryChipLabelWidth(text) <= categoryChipLabelMaxWidth(chipWidthPx);
}

export function categoryChipMaxWidth(colWidth: number, chipNumber: number = 3): number {
    const maxChipOnOneLineNumber = Math.ceil(chipNumber / 2);
    return Math.max(
        CATEGORY_CHIP_MIN_WIDTH,
        (colWidth - (maxChipOnOneLineNumber * CATEGORY_CHIP_ARROW_WIDTH) - (2 * maxChipOnOneLineNumber * CATEGORY_CHIP_GAP)) / maxChipOnOneLineNumber,
    );
}

export function categoryChipContainerMinWidth(chipMaxWidth: number, chipNumber: number = 5): number {
    const maxChipOnOneLineNumber = Math.ceil(chipNumber / 2);
    return (maxChipOnOneLineNumber * chipMaxWidth) + (maxChipOnOneLineNumber * CATEGORY_CHIP_ARROW_WIDTH) + (2 * maxChipOnOneLineNumber * CATEGORY_CHIP_GAP);
}

export function getCategoryColumnMinWidth(): number {
    return categoryChipContainerMinWidth(CATEGORY_CHIP_MIN_WIDTH) + 1;
}

export const DEFAULT_CATEGORY_COL_WIDTH = getCategoryColumnMinWidth();

export function doCategoryChipEllipsis(s: string, chipWidthPx: number): [string, boolean] {
    if (categoryChipTextFits(s, chipWidthPx)) return [s, false];
    if (s.length <= CATEGORY_CHIP_ELLIPSIS_START + CATEGORY_CHIP_ELLIPSIS_END) return [s, false];

    const truncated = `${s.substring(0, CATEGORY_CHIP_ELLIPSIS_START)}${ELLIPSIS}${s.substring(s.length - CATEGORY_CHIP_ELLIPSIS_END)}`;
    return [truncated, true];
}

export function getCategoryChipSx(chipMaxWidth?: number): SxProps<Theme> {
    return {
        mt: '2px',
        mb: '2px',
        maxWidth: chipMaxWidth ?? '100%',
        '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    };
}

export const categoryChipArrowStyle = {
    width: CATEGORY_CHIP_ARROW_WIDTH,
    height: CATEGORY_CHIP_ARROW_WIDTH,
    flexShrink: 0,
} as const;
